import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import { COLORS } from "@/utils/quizData";

import { getVoiceLang } from "@/utils/voicePref";
const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const CocokkanWarna = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(null);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);
  const totalRounds = 12;

  const nextQuestion = useCallback(() => {
    const correct = COLORS[Math.floor(Math.random() * COLORS.length)];
    const wrongs = [];
    while (wrongs.length < 3) {
      const w = COLORS[Math.floor(Math.random() * COLORS.length)];
      if (w.id !== correct.id && !wrongs.find(x => x.id === w.id)) wrongs.push(w);
    }
    const opts = [...wrongs, correct].sort(() => Math.random() - 0.5);
    setTarget(correct); setOptions(opts); setFeedback(null);
    setTimeout(() => { if (soundOn) speakText(`Click color ${correct.name}`); }, 300);
  }, [soundOn]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { nextQuestion(); }, []);

  const handlePick = (c) => {
    if (feedback) return;
    if (c.id === target.id) {
      setFeedback({ type: "ok", text: `Benar! ${c.name}!` });
      setScore(s => s + 1);
      if (soundOn) speakText(`Correct! Color ${c.name}!`);
    } else {
      setFeedback({ type: "wrong", text: `Itu ${c.name}` });
      if (soundOn) speakText(`The correct answer is ${target.name}`);
    }
    setTimeout(() => {
      if (round + 1 >= totalRounds) {
        setWon(true);
        recordGameWon("warna");
      } else {
        setRound(r => r + 1);
        nextQuestion();
      }
    }, 1500);
  };

  if (won) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="warna-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">🎨</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">Skor: {score}/{totalRounds}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setScore(0); setRound(0); setWon(false); nextQuestion(); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3 pt-3" data-testid="warna-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">🎨 {round + 1}/{totalRounds} • Skor: {score}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <motion.div key={round} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-card-strong rounded-2xl p-6 w-full text-center mb-3">
        <p className="font-fredoka text-gold-light/70 text-sm mb-2">Cari warna ini:</p>
        <p className="font-fredoka text-3xl text-gold font-bold mb-1">{target?.name}</p>
      </motion.div>

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3" data-testid={`warna-feedback-${feedback.type}`}>
            <span className={`font-fredoka font-bold text-base px-4 py-2 rounded-full ${feedback.type === "ok" ? "bg-green-500 text-white" : "bg-red-400 text-white"}`}>
              {feedback.type === "ok" ? "✅" : "❌"} {feedback.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-4 w-full">
        {options.map((c, i) => (
          <motion.button key={`${c.id}-${i}`} data-testid={`warna-opt-${i}`}
            onClick={() => handlePick(c)} disabled={!!feedback}
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
            className="rounded-2xl p-1 border-4 border-white/30 shadow-lg overflow-hidden"
            style={{ background: c.hex, minHeight: "clamp(90px, 14vw, 160px)" }}>
            <span className={`font-fredoka font-bold drop-shadow-lg ${["putih","kuning","kuning_lembut","krem","biru_muda","hijau_muda","merah_muda","ungu_muda","perak","oranye_muda","salmon"].includes(c.id) ? "text-slate-800" : "text-white"}`}
              style={{ fontSize: "clamp(1rem, 2.5vw, 1.75rem)" }}>
              {c.name}
            </span>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default CocokkanWarna;
